import React from 'react';
import Card from '../../../../components/Card';
import CardHeader from '../../../../components/CardHeader';
import styled from 'styled-components';
import PreparationFilters from '../PreparationFilters';
import ScatterGraph from '../../../../components/Charts/ScatterGraph';
import get from 'lodash/get';
import ExportButton from '../../../../components/Export';
import { I18n } from 'react-redux-i18n';

const PreparationBarGraphContainer = styled.div`
  font-family: ${(props) => props.theme.fonts.sfcompact_text};
  .exportButtonContainer {
    padding-bottom: 10px;
    margin-top: 10px;
  }

  .filterContainer {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 104px;
    background-color: rgba(173, 181, 189, 0.05);
    margin-bottom: 52px;
    padding-left: 20px;
  }

  .filterContainer:not(:last-children) {
    margin-right: 45px;
  }

  .calendarContainer {
    width: 300px;
    margin-right: 45px;
  }

  .filterCard {
    margin: 0px;
    margin-bottom: 20px;
  }

  .recharts-cartesian-axis-tick-value {
    font-size: 12px;
  }
`;

class PreparationBarGraph extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      hoverExport: false,
      wine: null,
      vintage: null,
      appellation: null,
      rank: null,
      color: null,
      minimumPrice: null,
      maximumPrice: null,
      competitors: [],
      renderLabelsPositions: {}
    };
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps != this.props &&
      (prevProps.client_wines != this.props.client_wines ||
        prevProps.data != this.props.data)
    ) {
      if (
        this.props.client_wines &&
        this.props.client_wines.length > 0 &&
        this.props.data &&
        this.props.data.length > 0
      ) {
        const rivalWineNames = [...this.props.rival_wines]
          .filter(
            (item) =>
              item.client_wine_id ===
              get(this.props.client_wines, '[0].wine_id')
          )
          .map((item) => item.wine);
        const competitorsData = [
          ...new Set(
            this.props.data
              .filter(
                (item) =>
                  rivalWineNames.includes(item.name) &&
                  item.vintage === get(this.props.client_wines, '[0].vintage')
              )
              .map((item) => item.name)
          )
        ];
        this.setState({
          wine: {
            value: get(this.props.client_wines, '[0].wine'),
            label: get(this.props.client_wines, '[0].wine')
          },
          vintage: {
            value: get(this.props.client_wines, '[0].vintage'),
            label: get(this.props.client_wines, '[0].vintage')
          },
          competitorsData: competitorsData,
          competitors: competitorsData
        });
      }
    }
  }

  getClientWineData(clientWine) {
    const data = this.props.data || [];
    return data.find(
      (item) =>
        item.name === clientWine.wine && item.vintage == clientWine.vintage
    );
  }

  getOptions() {
    const data = this.props.data || [];
    const client_wines = this.props.client_wines;
    const wineOptions = [...new Set(client_wines.map((item) => item.wine))].map(
      (item) => ({
        value: item,
        label: item
      })
    );

    const appellationOptions = [
      ...new Set(data.map((item) => item.appellation_name))
    ]
      .sort()
      .map((item) => ({ value: item, label: item }));

    const rankOptions = [...new Set(data.map((item) => item.classement_name))]
      .sort()
      .map((item) => ({ value: item, label: item }));

    const colorOptions = [...new Set(data.map((item) => item.color_name))].map(
      (item) => ({
        value: item,
        label: item
      })
    );

    const vintageOptions = [...new Set(data.map((item) => item.vintage))]
      .sort()
      .reverse()
      .map((item) => ({ value: item, label: item }));

    return {
      wine: wineOptions,
      vintage: vintageOptions,
      color: colorOptions,
      rank: [
        { label: I18n.t('notSelected'), value: 'none' },
        { label: I18n.t('placeholderAll2'), value: 'all' },
        ...rankOptions
      ],
      appellation: [
        { label: I18n.t('notSelected'), value: 'none' },
        { label: I18n.t('placeholderAll2'), value: 'all' },
        ...appellationOptions
      ]
    };
  }

  formatData(data) {
    let formattedData = [];
    let wineName;

    if (!data.length) {
      wineName = this.state.wine?.value;
      formattedData = [];
    } else {
      wineName = data[0].name;
      formattedData = [
        {
          name: `${data[0].name} ${data[0].vintage}`,
          color: '#e25d34',
          focused: true,
          value: [
            {
              x: Number(get(data[0], 'avg_price', 0)).toFixed(2),
              y: Number(get(data[0], 'wxr_note', 0)).toFixed(2),
              nameSet: `${data[0].name} ${data[0].vintage}`
            }
          ]
        }
      ];
    }

    let rivalsData = this.filterData(wineName);

    rivalsData = [...rivalsData, ...this.getDataFromRanks()];
    rivalsData = [...rivalsData, ...this.getDataFromAppellation()];

    formattedData.push({
      name: 'Benchmark',
      color: '#1f2b37',
      value: rivalsData
        .filter((item) => item.avg_price && item.wxr_note)
        .map((item) => ({
          x:
            !isNaN(get(item, 'avg_price')) && get(item, 'avg_price') > 0
              ? get(item, 'avg_price').toFixed(2)
              : 0,
          y:
            !isNaN(get(item, 'wxr_note', 0)) && get(item, 'wxr_note', 0) > 0
              ? get(item, 'wxr_note', 0).toFixed(2)
              : 0,
          nameSet: `${item.name} ${item.vintage}`
        }))
    });
    return formattedData;
  }

  filterData(clientWineName, filterCompetitors = true) {
    const { data } = this.props;

    let filteredData = [...data].filter((item) => item.avg_price);
    const { wine, color, vintage, minimumPrice, maximumPrice, competitors } =
      this.state;

    if (!clientWineName) {
      filteredData = wine
        ? filteredData.filter((item) => item.name === wine.value)
        : [];
    } else {
      const rivalWineNames = [...this.props.rival_wines]
        .filter(
          (item) =>
            item.client_wine_id ===
            get(
              this.props.client_wines.find(
                (clientWine) => clientWine.wine === clientWineName
              ),
              'wine_id'
            )
        )
        .map((item) => item.wine);
      filteredData = filteredData.filter((item) =>
        rivalWineNames.includes(item.name)
      );

      if (filterCompetitors) {
        filteredData = filteredData.filter((item) =>
          competitors.includes(item.name)
        );
      }
    }

    filteredData = vintage
      ? filteredData.filter((item) => item.vintage === vintage.value)
      : wine
        ? []
        : filteredData.filter((item) => item.vintage === data[0].vintage);

    filteredData = color
      ? filteredData.filter((item) => item.color_name === color.value)
      : filteredData;

    filteredData = minimumPrice
      ? filteredData.filter((item) => item.avg_price >= minimumPrice)
      : filteredData;
    filteredData =
      Number(maximumPrice) && Number(maximumPrice) !== 0
        ? filteredData.filter((item) => item.avg_price <= maximumPrice)
        : filteredData;

    return filteredData;
  }

  getDataFromRanks() {
    const { data } = this.props;
    const {
      rank,
      wine,
      competitors,
      vintage,
      minimumPrice,
      color,
      maximumPrice
    } = this.state;
    let dataFilterByWineColorPrice = [...data].filter(
      (item) =>
        (!vintage || item.vintage === vintage.value) &&
        (!color || item.color_name === color.value) &&
        (!minimumPrice || item.avg_price >= minimumPrice) &&
        (!maximumPrice ||
          Number(maximumPrice) === 0 ||
          item.avg_price <= maximumPrice)
    );

    return dataFilterByWineColorPrice.filter((item) => {
      if (
        !wine ||
        competitors.includes(item.name) ||
        wine.label === item.name
      ) {
        return false;
      }
      if (!rank || rank.value === 'none') {
        return false;
      }
      if (rank && rank.value === 'all') {
        return !!item.classement_name;
      }
      return rank && rank.value === item.classement_name;
    });
  }

  getDataFromAppellation() {
    const { data } = this.props;
    const {
      appellation,
      wine,
      competitors,
      vintage,
      minimumPrice,
      color,
      maximumPrice
    } = this.state;
    let dataFilterByWineColorPrice = [...data].filter(
      (item) =>
        (!vintage || item.vintage === vintage.value) &&
        (!color || item.color_name === color.value) &&
        (!minimumPrice || item.avg_price >= minimumPrice) &&
        (!maximumPrice ||
          Number(maximumPrice) === 0 ||
          item.avg_price <= maximumPrice)
    );

    return dataFilterByWineColorPrice.filter((item) => {
      if (
        !wine ||
        competitors.includes(item.name) ||
        wine.label === item.name
      ) {
        return false;
      }
      if (!appellation || appellation.value === 'none') {
        return false;
      }
      if (appellation && appellation.value === 'all') {
        return !!item.appellation_name;
      }
      return appellation && appellation.value === item.appellation_name;
    });
  }

  async handleChange(field, value) {
    const { wine, vintage, minimumPrice, maximumPrice, appellation, rank } =
      this.state;

    if (field === 'minimumPrice') {
      if (
        !maximumPrice &&
        (!rank || rank.value === 'none') &&
        (!appellation || appellation.value === 'none')
      ) {
        this.setState({
          appellation: { label: I18n.t('placeholderAll2'), value: 'all' }
        });
      }
    }
    if (field === 'maximumPrice') {
      if (
        !minimumPrice &&
        (!rank || rank.value === 'none') &&
        (!appellation || appellation.value === 'none')
      ) {
        this.setState({
          appellation: { label: I18n.t('placeholderAll2'), value: 'all' }
        });
      }
    }

    if (field === 'wine' || field === 'vintage') {
      await this.setState({
        wine: field === 'wine' ? value : wine,
        vintage: field === 'vintage' ? value : vintage,
        appellation: null,
        rank: null,
        color: null,
        minimumPrice: 0,
        maximumPrice: 0
      });
    } else {
      await this.setState({ [field]: value });
    }
    const rivalWineNames = [...this.props.rival_wines].map((item) => item.wine);
    this.setState({
      competitors: [
        ...new Set(
          this.filterData(wine ? wine.value : null, false).map(
            (item) => item.name
          )
        )
      ].filter((item) => rivalWineNames.includes(item)),
      competitorsData: [
        ...new Set(
          this.filterData(wine ? wine.value : null, false).map(
            (item) => item.name
          )
        )
      ].filter((item) => rivalWineNames.includes(item))
    });
  }

  render() {
    const options = this.getOptions();
    const filteredData = this.filterData();
    const { competitorsData, vintage } = this.state;
    const formattedData = this.formatData(filteredData);

    return (
      <PreparationBarGraphContainer id="scatter-graph">
        <Card>
          <CardHeader
            title={I18n.t('primeur.compare.scatter.title')}
            fontSize="20px"
            iconRight
            rightComponents={
              <ExportButton
                jpg
                pdf
                xls
                path="/api/v1/clients/me/primeur/compare/xls/"
                options={{ vintage: vintage ? vintage.label : '' }}
                name="comparaison-prix-primeur-score"
                targetElement="#scatter-graph"
              />
            }
          />
          <div className="ignored-export">
            <PreparationFilters
              selections={this.state}
              options={options}
              onChange={(field, value) => this.handleChange(field, value)}
              onChangeCompetitors={(labels) =>
                this.setState({ competitors: labels })
              }
              dataCompetitors={competitorsData}
            />
          </div>
          <ScatterGraph
            data={formattedData}
            currency="EUR"
            tooltipContent={{
              xLabel: I18n.t('primeur.compare.scatter.average'),
              yLabel: 'WxR'
            }}
          />
        </Card>
      </PreparationBarGraphContainer>
    );
  }
}

export default PreparationBarGraph;
